/** Dice rules. The RNG is injected so the server can use crypto.randomInt and tests can be deterministic. */
export const DIE_SIDES = [4, 6, 8, 10, 12, 20, 100];
export const MAX_DICE = 20;
export const MAX_MODIFIER = 50;
export function validateDiceSpec(spec) {
    if (!Number.isInteger(spec.count) || spec.count < 1 || spec.count > MAX_DICE)
        return `count must be 1-${MAX_DICE}`;
    if (!DIE_SIDES.includes(spec.sides))
        return `sides must be one of ${DIE_SIDES.join(", ")}`;
    if (!Number.isInteger(spec.modifier) || Math.abs(spec.modifier) > MAX_MODIFIER)
        return `modifier must be -${MAX_MODIFIER}..${MAX_MODIFIER}`;
    if (!["normal", "advantage", "disadvantage"].includes(spec.mode))
        return "invalid mode";
    return null;
}
export function formatFormula(spec) {
    const mod = spec.modifier === 0 ? "" : spec.modifier > 0 ? `+${spec.modifier}` : `${spec.modifier}`;
    const mode = spec.mode === "advantage" ? " adv" : spec.mode === "disadvantage" ? " dis" : "";
    return `${spec.count}d${spec.sides}${mod}${mode}`;
}
export function rollDice(spec, rng) {
    const err = validateDiceSpec(spec);
    if (err)
        throw new Error(err);
    const mode = spec.sides === 20 && spec.count === 1 ? spec.mode : "normal";
    const effective = { ...spec, mode };
    const die = () => rng(1, spec.sides + 1);
    if (mode !== "normal") {
        const a = die();
        const b = die();
        const keepA = mode === "advantage" ? a >= b : a <= b;
        const kept = keepA ? a : b;
        const dropped = keepA ? b : a;
        return { formula: formatFormula(effective), dice: [kept], dropped: [dropped], total: kept + spec.modifier, mode };
    }
    const dice = Array.from({ length: spec.count }, die);
    return {
        formula: formatFormula(effective),
        dice,
        dropped: [],
        total: dice.reduce((s, d) => s + d, 0) + spec.modifier,
        mode,
    };
}
/**
 * Parse a simple formula: "d20", "1d20+5", "2d6 - 1", "d20 adv", "1d20+3 disadvantage", "+4" (=> 1d20+4).
 * Only one dice group is supported. Returns null if it can't be parsed or is out of range.
 */
export function parseFormula(input) {
    let s = input.trim().toLowerCase();
    let mode = "normal";
    const modeMatch = /\s*\b(adv|advantage|dis|disadvantage)\b\s*$/.exec(s);
    if (modeMatch) {
        mode = modeMatch[1].startsWith("adv") ? "advantage" : "disadvantage";
        s = s.slice(0, modeMatch.index);
    }
    s = s.replace(/\s+/g, "");
    let m = /^(\d*)d(\d+)([+-]\d+)?$/.exec(s);
    let spec = null;
    if (m) {
        spec = { count: m[1] ? Number(m[1]) : 1, sides: Number(m[2]), modifier: m[3] ? Number(m[3]) : 0, mode };
    }
    else if ((m = /^([+-]\d+)$/.exec(s))) {
        spec = { count: 1, sides: 20, modifier: Number(m[1]), mode };
    }
    if (!spec || validateDiceSpec(spec))
        return null;
    return spec;
}
/**
 * 5e: advantage and disadvantage from different sources cancel to a normal roll (no matter how many of each).
 * Missing/unknown values count as normal.
 */
export function combineModes(...modes) {
    const adv = modes.includes("advantage");
    const dis = modes.includes("disadvantage");
    return adv && !dis ? "advantage" : dis && !adv ? "disadvantage" : "normal";
}
/**
 * Human-readable result showing both d20s for advantage/disadvantage, e.g. "Disadvantage: 14, 6 -> 6 + 3 = 9",
 * "Advantage: 6, 14 -> 14 + 3 = 17", "2d6+3: 4, 5 + 3 = 12".
 */
/** The natural (kept) d20 of a single-d20 test, else null. With advantage/disadvantage only the KEPT die counts. */
export function naturalD20(r) {
    const single = r.sides !== undefined ? r.sides === 20 && (r.count ?? 1) === 1 : /^1?d20\b/.test(r.formula ?? "") && r.dice.length === 1;
    return single && r.dice.length === 1 ? r.dice[0] : null;
}
/** "NATURAL 1 - critical fail" / "NATURAL 20 - critical" / "" for the kept d20. */
export function critTag(r) {
    const n = naturalD20(r);
    return n === 1 ? "NATURAL 1 - critical fail" : n === 20 ? "NATURAL 20 - critical" : "";
}
export function describeRoll(r) {
    const tag = critTag(r);
    return describeDice(r) + (tag ? ` - ${tag}` : "");
}
function describeDice(r) {
    const sum = r.dice.reduce((s, d) => s + d, 0);
    const mod = r.modifier ?? r.total - sum;
    const tail = mod === 0 ? ` = ${r.total}` : ` ${mod > 0 ? "+" : "-"} ${Math.abs(mod)} = ${r.total}`;
    if (r.mode !== "normal" && r.dropped.length) {
        const both = r.mode === "disadvantage" ? [Math.max(r.dice[0], r.dropped[0]), Math.min(r.dice[0], r.dropped[0])] : [Math.min(r.dice[0], r.dropped[0]), Math.max(r.dice[0], r.dropped[0])];
        return `${r.mode === "advantage" ? "Advantage" : "Disadvantage"}: ${both.join(", ")} -> ${r.dice[0]}${tail}`;
    }
    return `${r.formula ? `${r.formula}: ` : ""}${r.dice.join(", ")}${tail}`;
}
//# sourceMappingURL=dice.js.map